<i18n src="./translations"></i18n>

<template>
  <div
    ref="rootElement"
    :class="[
      classes,
      isMobile
        ? 'addedToCartNotification--mobile'
        : 'addedToCartNotification--desktop',
    ]"
    data-testid="minicart-addedToCartNotification"
    :data-preload="[$options.name]"
    :style="stickyProps"
  >
    <div class="triangle">
      <Icon name="triangle" width="25" height="25" />
    </div>

    <span class="header header--mobile">
      <Icon
        class="check-icon"
        name="added_to_cart_check"
        height="20"
        width="20"
      />

      <span class="header-text">
        {{ $t('added-to-cart-notification.cart.added') }}
      </span>

      <button
        type="button"
        class="close-button"
        data-testid="minicart-closeNotification"
        :title="$t('added-to-cart-notification.cart.close')"
        :aria-label="$t('added-to-cart-notification.cart.close')"
        @click="handleClose()"
      >
        <Icon name="close" height="24" width="24" />
      </button>
    </span>

    <span class="header header--desktop">
      <Icon
        class="header-icon"
        name="added_to_cart_check"
        height="22"
        width="22"
      />

      <span class="header-text">
        {{ $t('added-to-cart-notification.cart.added') }}
      </span>
    </span>

    <div v-show="!isMobile" class="body">
      <ProductTile
        v-if="product"
        :product="product"
        :fixed-quantity="true"
        show-image
      />
    </div>

    <div class="footer">
      <Button
        class="ga-addedToCartNotification-checkoutBtn gtm--addedToCartNotification-checkoutBtn"
        data-testid="minicart-goToCheckout"
        :modifiers="['primary-light', 'full']"
        :title="$t('added-to-cart-notification.cart.fast-checkout')"
        :href="frontFacingPath('checkout.checkout.details')"
        :on-click="checkUnavailableProducts"
      />
      <Button
        v-if="!isFastCheckout"
        class="ga-addedToCartNotification-cartBtn gtm--addedToCartNotification-cartBtn"
        data-testid="minicart-goToCart"
        :modifiers="['secondary', 'full']"
        :title="$t('added-to-cart-notification.cart.view')"
        :href="frontFacingPath('checkout.cart.details')"
      />
    </div>

    <Dialog
      ref="unavailableDialog"
      :click-outside="true"
      v-bind="unavailableDialogConfiguration"
    >
      {{ $t(`mini-cart.${dialogType}.message`) }}
    </Dialog>
  </div>
</template>

<script>
import Icon from 'Components/00-generated/Icon';
import ProductTile from 'Components/02-molecules/product-tile/ProductTile';
import Button from 'Components/01-atoms/button/Button';
import Dialog from 'Components/02-molecules/dialog/Dialog.vue';
import globalMixin from 'Libs/mixins/globalMixin';
import { mapState, mapGetters, mapActions } from 'vuex';

export default {
  name: 'AddedToCartNotification',
  components: {
    ProductTile,
    Icon,
    Button,
    Dialog,
  },
  mixins: [globalMixin],
  props: {
    product: {
      type: Object,
      default: null,
    },
  },
  data: () => ({
    stickyDesktopProps: null,
    offsetTop: 0,
  }),
  computed: {
    ...mapState('core', {
      isMobile: ({ viewport }) => viewport.range !== 'desktop',
    }),
    ...mapState('miniCart', {
      unavailableProductCount: (state) =>
        state.data.unavailableProductCount || 0,
      totalProductCount: (state) => state.data.totalProductCount || 0,
    }),
    ...mapGetters('core', ['isFastCheckout']),
    stickyProps() {
      return this.isMobile ? null : this.stickyDesktopProps;
    },
    dialogType() {
      return this.unavailableProductCount === this.totalProductCount
        ? 'unavailable_dialog'
        : 'partly_unavailable_dialog';
    },
    unavailableDialogConfiguration() {
      let dialogConfig = {
        title: this.$t(`mini-cart.${this.dialogType}.title`),
      };

      if (this.dialogType === 'partly_unavailable_dialog') {
        dialogConfig = {
          ...dialogConfig,
          'cancel-button': {
            title: this.$t('mini-cart.partly_unavailable_dialog.cancel'),
          },
          'confirm-button': {
            title: this.$t('mini-cart.partly_unavailable_dialog.accept'),
            onClick: () => {
              window.location.assign(
                this.frontFacingPath('checkout.checkout.details')
              );
            },
          },
        };
      }
      return dialogConfig;
    },
  },
  mounted() {
    const { offsetTop, offsetHeight } = this.$refs.rootElement.parentElement;
    this.offsetTop = offsetTop + offsetHeight + 15; // 15 - height of the little arrow
    this.handleScroll();

    window.addEventListener('scroll', this.handleScroll);
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll);
  },
  methods: {
    handleScroll() {
      if (!this.$refs.rootElement) {
        return;
      }

      // Remove sticky props
      if (window.pageYOffset < this.offsetTop) {
        this.stickyDesktopProps = null;
        this.removeModifier('stickyOnDesktop');
        return;
      }

      // Do nothing when the sticky props are already computed&applied
      if (this.stickyDesktopProps) {
        return;
      }

      const {
        left,
        width,
      } = this.$refs.rootElement.parentElement.getBoundingClientRect();
      this.addModifier('stickyOnDesktop');
      this.stickyDesktopProps = {
        top: '15px', // 15 - height of the little arrow
        right: document.body.offsetWidth - left - width + 'px',
        position: 'fixed',
      };
    },
    ...mapActions('header', ['closeMiniCartAndNotification']),
    ...mapActions('dataLayer', ['trackMiniCart2Checkout']),
    handleClose() {
      this.closeMiniCartAndNotification();
    },
    checkUnavailableProducts(e) {
      e.preventDefault();

      if (this.unavailableProductCount && this.unavailableProductCount > 0) {
        this.$refs.unavailableDialog.show();
      } else {
        const url = this.frontFacingPath('checkout.checkout.details');
        this.trackMiniCart2Checkout().finally(() => this.redirectTo(url));
      }
    },
    redirectTo(url) {
      window.location.href = url;
    },
  },
};
</script>

<style scoped lang="scss">
@import 'variables/breakpoints';
@import 'mixins';

.addedToCartNotification {
  @include dropShadow;

  background-color: var(--color-white);

  z-index: 6;

  &--stickyOnDesktop {
    .triangle {
      display: block;
    }

    &::before {
      display: none;
    }
  }

  &--desktop {
    position: absolute;
    top: 48px;
    right: 0;
    width: 320px;

    .header--desktop {
      display: flex;
    }

    .header-text {
      font-size: var(--font-size-M);
      font-weight: bold;
      padding: 30px 0;
      display: block;
    }

    .header-icon {
      display: block;
      margin-left: 0;
      margin-right: var(--space-1);
    }
  }

  &--mobile {
    position: fixed;
    top: auto;
    bottom: 0;
    left: 0;
    right: 0;
    width: unset;
    border-top: 1px solid var(--color-alto);
    box-shadow: 0 -3px 10px 0 rgba(0, 0, 0, 0.1);
    padding-bottom: env(safe-area-inset-bottom, 0);

    // The desktop popup's pointer triangle has nothing to point at down here.
    .triangle {
      display: none;
    }

    .header--mobile {
      display: flex;
    }

    .header {
      justify-content: flex-start;
      padding: var(--space-3);
    }

    svg.check-icon {
      flex-shrink: 0;
      margin-left: 0;
      fill: var(--color-rebranding-black-out);
    }

    .header-text {
      display: block;
      flex-grow: 1;
      margin-left: var(--space-1);
      font-size: var(--font-size-SM);
      font-weight: bold;
      text-align: left;
    }

    .close-button {
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
      padding: 0;
      background: none;
      border: none;
      cursor: pointer;

      svg {
        margin-left: 0;
        fill: var(--color-mine-shaft);
      }
    }

    .footer {
      // The header row already carries space-3 of bottom padding, so this
      // omits its own top padding to land on a single space-3 gap overall.
      padding: 0 var(--space-3) var(--space-3);

      ::v-deep .button {
        min-height: 48px;
      }

      .ga-addedToCartNotification-checkoutBtn {
        margin-bottom: var(--space-2);
      }
    }
  }
}

.triangle {
  display: none;
  position: absolute;
  transform: scale(2, 1);
  top: -15px;
  right: 25px;

  svg {
    fill: var(--color-white);
    filter: drop-shadow(0 1px 5px rgba(0, 0, 0, 0.3));
  }
}

.header {
  display: flex;
  justify-content: center;
  align-items: center;

  position: relative;

  background-color: var(--color-white);
  color: var(--color-black);

  svg {
    margin-left: var(--space-4);
    fill: var(--color-black);
  }
}

.header--desktop,
.header--mobile {
  display: none;
}

.body {
  background-color: var(--color-white);
  border-top: 1px solid var(--color-alto);
}

.footer {
  padding: 15px;
  background: var(--color-white);

  .ga-addedToCartNotification-checkoutBtn {
    margin-bottom: var(--space-1);
  }
}
</style>
